import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { Badge, Button, ListPage, TableSkeleton } from "@redipos/ui";
import type { ConsoleTenantHealthRow } from "@redipos/contracts";
import { operacionesApi, type ModuleDriftRow } from "../api.js";
import { CATEGORIA_BADGE, diasLabel, ETAPAS, etapaInfo } from "../lib/activationStages.js";

const CATEGORIA_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Cliente + prospecto (default)" },
  { value: "cliente", label: "Solo clientes" },
  { value: "prospecto", label: "Solo prospectos" },
  { value: "curso", label: "Curso (ruido)" },
  { value: "interno", label: "Interno (ruido)" },
  { value: "todos", label: "Todos" },
];

/** Clientes activos cuyo plan y módulos activados no coinciden (sirve para no olvidar activar lo que pagaron). */
function ModuleDriftPanel() {
  const [rows, setRows] = useState<ModuleDriftRow[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = () =>
    operacionesApi
      .moduleDrift()
      .then(setRows)
      .catch(() => setRows([]));
  useEffect(() => {
    void load();
  }, []);

  const activar = async (r: ModuleDriftRow) => {
    const id = `${r.tenantId}:${r.module}`;
    setBusy(id);
    setErr(null);
    try {
      await operacionesApi.setModuleStatus(r.tenantId, r.module, true);
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Error");
    } finally {
      setBusy(null);
    }
  };

  if (rows.length === 0) return null;
  return (
    <div className="rounded-card border border-warning/40 bg-warning-soft/40 p-4" role="region" aria-label="Módulos desalineados">
      <p className="text-sm font-semibold text-fg">Módulos desalineados con el plan ({rows.length})</p>
      <p className="mb-3 text-xs text-fg-muted">
        “Incluido sin activar”: el plan lo trae pero el cliente no lo ve. “Activo sin plan”: está encendido pero el plan
        no lo incluye, así que queda bloqueado.
      </p>
      {err ? (
        <p className="mb-2 text-sm text-danger" role="alert">
          {err}
        </p>
      ) : null}
      <ul className="divide-y divide-border">
        {rows.map((r) => (
          <li key={`${r.tenantId}:${r.module}`} className="flex flex-wrap items-center gap-3 py-2 text-sm">
            <Link to={`/clientes/${r.tenantId}`} className="font-medium text-brand-500 hover:text-brand-400">
              {r.tenantName}
            </Link>
            <span className="text-fg-muted">{r.label}</span>
            <Badge variant={r.kind === "incluido_sin_activar" ? "warning" : "danger"} size="sm">
              {r.kind === "incluido_sin_activar" ? "Incluido sin activar" : "Activo sin plan"}
            </Badge>
            {r.apagadoExplicito ? <span className="text-xs text-fg-subtle">apagado a propósito</span> : null}
            {r.kind === "incluido_sin_activar" ? (
              <Button
                type="button"
                variant="secondary"
                className="ml-auto"
                loading={busy === `${r.tenantId}:${r.module}`}
                loadingLabel="Activando..."
                onClick={() => void activar(r)}
              >
                Activar
              </Button>
            ) : (
              <Link to={`/clientes/${r.tenantId}/facturacion`} className="ml-auto text-sm font-medium text-brand-500 hover:text-brand-400">
                Revisar plan
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function TenantHealthPage() {
  const [rows, setRows] = useState<ConsoleTenantHealthRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoria, setCategoria] = useState("");

  useEffect(() => {
    setLoading(true);
    operacionesApi
      .tenantHealth(categoria || undefined)
      .then(setRows)
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [categoria]);

  const conteoPorEtapa = useMemo(() => {
    const acc: Record<string, number> = {};
    for (const r of rows) acc[r.etapa] = (acc[r.etapa] || 0) + 1;
    return acc;
  }, [rows]);

  const metrics = ETAPAS.map((e) => ({
    key: e.key,
    label: e.metricLabel,
    value: loading ? "—" : conteoPorEtapa[e.key] || 0,
  }));

  const sorted = useMemo(() => {
    const rank = Object.fromEntries(ETAPAS.map((e, i) => [e.key, i]));
    return [...rows].sort((a, b) => (rank[a.etapa] ?? 0) - (rank[b.etapa] ?? 0));
  }, [rows]);

  return (
    <ListPage
      title="Salud y activación"
      subtitle="Embudo por tenant: registro → onboarding → primer producto → primera venta → actividad reciente"
      metrics={metrics}
      beforeSection={<ModuleDriftPanel />}
      sectionTitle="Tenants"
      sectionSubtitle="Ordenado por etapa del embudo — los que necesitan atención aparecen primero"
      actions={
        <select
          value={categoria}
          onChange={(e) => setCategoria(e.target.value)}
          className="rounded-input bg-surface border border-border text-sm text-fg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500/40 focus:border-brand-500"
        >
          {CATEGORIA_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      }
    >
      {loading ? (
        <TableSkeleton rows={6} cols={6} />
      ) : (
        <div className="table-x-scroll">
          <table className="w-full text-sm min-w-[880px]">
            <thead>
              <tr className="bg-surface-elevated text-left border-b border-border">
                <th className="px-4 py-3">
                  <span className="text-xs font-medium uppercase tracking-wide text-fg-subtle">Organización</span>
                </th>
                <th className="px-4 py-3 w-24">
                  <span className="text-xs font-medium uppercase tracking-wide text-fg-subtle">Categoría</span>
                </th>
                <th className="px-4 py-3 w-56">
                  <span className="text-xs font-medium uppercase tracking-wide text-fg-subtle">Etapa</span>
                </th>
                <th className="px-4 py-3 w-28">
                  <span className="text-xs font-medium uppercase tracking-wide text-fg-subtle">Registro</span>
                </th>
                <th className="px-4 py-3 w-24">
                  <span className="text-xs font-medium uppercase tracking-wide text-fg-subtle">Productos</span>
                </th>
                <th className="px-4 py-3 w-32">
                  <span className="text-xs font-medium uppercase tracking-wide text-fg-subtle">Última venta</span>
                </th>
                <th className="px-4 py-3 w-24 text-right"> </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-fg-subtle">
                    Sin resultados
                  </td>
                </tr>
              ) : (
                sorted.map((r) => {
                  const info = etapaInfo(r.etapa);
                  return (
                    <tr key={r.tenantId} className="hover:bg-surface-elevated/60 transition-colors align-top">
                      <td className="px-4 py-3">
                        <p className="font-medium text-fg">{r.nombre}</p>
                        {r.nota ? <p className="text-[11px] text-fg-subtle mt-0.5">{r.nota}</p> : null}
                        {r.activo === false ? (
                          <Badge variant="danger" size="sm" className="mt-1">
                            Suspendida
                          </Badge>
                        ) : null}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={CATEGORIA_BADGE[r.categoria] ?? "muted"} size="sm">
                          {r.categoria}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={info.variant} size="sm">
                          {info.label}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-fg-muted whitespace-nowrap">
                        Hace {r.diasDesdeRegistro} días
                        {!r.onboardingCompletado ? (
                          <p className="text-[11px] text-warning mt-1">Onboarding pendiente</p>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-fg-muted">{r.totalProductos}</td>
                      <td className="px-4 py-3 text-fg-muted whitespace-nowrap">
                        {diasLabel(r.diasDesdeUltimaVenta)}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <Link
                          to={`/clientes/${r.tenantId}`}
                          className="inline-flex items-center gap-1 text-brand-500 hover:text-brand-400 text-sm font-medium"
                        >
                          Ver
                          <ChevronRight className="w-4 h-4" />
                        </Link>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </ListPage>
  );
}
